import React, { useEffect, useState } from "react";
import "../styles.scss";
import { Text } from "@amzn/stencil-react-components/text";
import { Row } from "@amzn/stencil-react-components/layout";
import {
  Button,
  ButtonSize,
  ButtonVariant,
} from "@amzn/stencil-react-components/button";
import { StatusContainer } from "./status-container";
import { NheDetails } from "../../../utility/application-data";
import {
  IconCalendarFill,
  IconClockFill,
} from "@amzn/stencil-react-components/icons";
import { processTime } from "../../../utility/time-processor";
import useArb from "../../../hooks/use-arb";
import { getSettings, Stage } from "../../../helpers/get-settings";
import { MLS_REQUIRED_LANGUAGE } from "../../../config/feature-control";
import { Link } from "@amzn/stencil-react-components/link";
import ICalendarLink from "react-icalendar-link";
import moment from "moment";
import "moment-timezone";
import { GetCalendarEvent } from "../../../utility/calendar-event";
import { useNavigatorWithURLReady } from "../../../hooks/use-navigator";
import { popoverTextColor } from "../../../config/palette";

interface PreHireEventHeaderProps {
  nheDetails: NheDetails;
  onViewDetailsClick: () => void;
  supportedLanguage: string;
  eligibleForAppointmentRescheduling: boolean;
  appointmentSelfServiceURL: string;
}

export const PreHireEventHeader = ({
  nheDetails,
  supportedLanguage,
  onViewDetailsClick,
  eligibleForAppointmentRescheduling,
  appointmentSelfServiceURL,
}: PreHireEventHeaderProps) => {
  const bundle = useArb("HVHChecklistPantherResource");

  //reason why ASH has to process time and date is because NHE doesnt handle the format by locale. we will need to format it into a valid date object to be translated by panther
  const startTime = nheDetails.nheTimeRange?.split("-")[0];
  const processedStartTime = processTime(startTime);

  const startDateAndStartTime =
    nheDetails.nheDate?.toString() + " " + processedStartTime;

  const plainStartDateAndStartTime = new Date(startDateAndStartTime);

  const timeFormat = "MMM DD, YYYY hh:mm A";

  const dateTzWithStartTime = moment.tz(
    startDateAndStartTime,
    timeFormat,
    nheDetails.timezone
  );

  const currDate = new Date();
  const currentDateTime = moment.tz(currDate, nheDetails.timezone);

  const dayDiff = dateTzWithStartTime.diff(currentDateTime, "day");

  const dateTranslated = bundle.formatMessage(
    "Checklist-AppointmentCard-Month-Day-Year",
    {
      dateParam: plainStartDateAndStartTime,
    }
  );

  const timeTranslated = bundle.formatMessage(
    "Checklist-AppointmentCard-Time",
    {
      dateParam: plainStartDateAndStartTime,
    }
  );

  const reschedule = bundle.getMessage("Checklist-AppointmentCard-ButtonText2");
  const cannotAttend = bundle.formatMessage(
    "Checklist-PreHireDetails-CannotAttend"
  );

  const countdownMsg =
    dayDiff > 0
      ? bundle.formatMessage("Checklist-AppointmentCard-Countdown1", {
          days: dayDiff,
        })
      : bundle.formatMessage("Checklist-AppointmentCard-Countdown2");

  const formattedAppointmentSupportedLanguageMsg =
    bundle.getMessage("Checklist-AppointmentCard-Language") +
    ": " +
    supportedLanguage;

  const addToCalendar = bundle.formatMessage(
    "Checklist-AppointmentCard-ButtonText3"
  );
  const viewDetails = bundle.formatMessage(
    "Checklist-AppointmentCard-ButtonText1"
  );
  const appointmentTitle = bundle.formatMessage(
    "Checklist-CardControlled-PreHireTitle"
  );

  const googlePlaceURL = "http://maps.google.com/?q=";

  const [stage, setStage] = useState<Stage>();
  useEffect(() => {
    getSettings().then((settings) => setStage(settings.stage));
  });

  const event = GetCalendarEvent(
    nheDetails,
    appointmentTitle,
    nheDetails.address
  );

  return (
    <StatusContainer
      largeViewPortImage={require("./calendar-large-viewport.png").default}
      smallViewPortImage={require("./calendar-small-viewport.png").default}
    >
      <Text fontSize={"T400"}>{countdownMsg}</Text>
      <Row gridGap={"1rem"}>
        <Row gridGap={"0.3rem"} alignItems={"center"}>
          <IconCalendarFill />
          <Text fontSize={"T200"}>{dateTranslated}</Text>
        </Row>
        <Row gridGap={"0.3rem"} alignItems={"center"}>
          <IconClockFill />
          <Text fontSize={"T200"}>
            {timeTranslated +
              " " +
              dateTzWithStartTime.zoneAbbr().substring(-2)}
          </Text>
        </Row>
      </Row>
      {stage && MLS_REQUIRED_LANGUAGE[stage] && (
        <Text fontSize={"T200"}>
          {formattedAppointmentSupportedLanguageMsg}
        </Text>
      )}
      <Link
        fontSize={"T200"}
        href={googlePlaceURL + nheDetails.address}
        target={"_blank"}
      >
        {nheDetails.address}
      </Link>
      <Row gridGap={"1rem"} alignItems="center">
        {eligibleForAppointmentRescheduling && (
          <>
            <Text style={{ fontStyle: "italic" }} fontSize={"T200"}>
              {cannotAttend}
            </Text>
            <Text
              className="text-button"
              color={popoverTextColor}
              fontSize="T200"
              textDecoration="underline"
              onClick={useNavigatorWithURLReady(appointmentSelfServiceURL)}
            >
              {reschedule}
            </Text>
          </>
        )}
      </Row>
      <Row gridGap={"1rem"}>
        <ICalendarLink
          filename={appointmentTitle}
          event={event}
          className={"add-to-calendar"}
        >
          <Button size={ButtonSize.Small}>{addToCalendar}</Button>
        </ICalendarLink>
        <Button
          variant={ButtonVariant.Primary}
          size={ButtonSize.Small}
          onClick={onViewDetailsClick}
        >
          {viewDetails}
        </Button>
      </Row>
    </StatusContainer>
  );
};
